import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Group, Filter, Check, ArrowRight, FastArrowRight, ViewGrid } from 'iconoir-react';
import { PublicFooter } from './PublicFooter';

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <ViewGrid width={32} height={32} strokeWidth={2} />,
      title: 'Tableaux Kanban intuitifs',
      description: 'Organisez vos projets avec des tableaux visuels et des colonnes personnalisables.'
    },
    {
      icon: <Group width={32} height={32} strokeWidth={2} />,
      title: 'Collaboration en équipe',
      description: 'Invitez vos collaborateurs et travaillez ensemble en temps réel.'
    },
    {
      icon: <Filter width={32} height={32} strokeWidth={2} />,
      title: 'Filtres puissants',
      description: 'Trouvez rapidement vos cartes avec des filtres avancés par étiquettes, membres et dates.'
    },
    {
      icon: <Check width={32} height={32} strokeWidth={2} />,
      title: 'Suivi des activités',
      description: 'Gardez une trace de toutes les modifications avec un historique complet.'
    }
  ];

  const benefits = [
    'Développé, maintenu et hébergé en France',
    '100% conforme RGPD - Vos données restent en Europe',
    'Jusqu\'à 70% moins cher que Trello ou Asana',
    'Toutes les fonctionnalités des leaders du marché',
    'Interface moderne et ultra-responsive',
    'Personnalisation complète (étiquettes, couleurs, rôles)',
    'Commentaires, pièces jointes et export PDF',
    'Gestion avancée des permissions et membres',
    'API REST complète pour vos intégrations personnalisées'
  ];

  return (
    <div className="landing-page">
      {/* Header / Navigation */}
      <header className="landing-header">
        <div className="landing-container">
          <div className="landing-nav">
            <div className="landing-logo">
              <img src="/assets/ventilio.png" alt="Ventilio" className="landing-logo-img" />
            </div>
            <div className="landing-nav-actions">
              <Link to="/tarifs" className="btn-landing-secondary">
                Tarifs
              </Link>
              <button
                className="btn-landing-secondary"
                onClick={() => navigate('/login')}
              >
                Connexion
              </button>
              <button 
                className="btn-landing-primary"
                onClick={() => navigate('/register')}
              >
                Commencer gratuitement
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-container">
          <div className="landing-hero-content">
            <h1 className="landing-hero-title">
              Gérez vos projets avec simplicité et efficacité
            </h1>
            <p className="landing-hero-subtitle">
              Ventilio est une solution Kanban moderne pour organiser vos tâches, collaborer avec votre équipe 
              et suivre l'avancement de vos projets en temps réel.
            </p>
            <div className="landing-hero-actions">
              <button 
                className="btn-landing-hero-primary"
                onClick={() => navigate('/register')}
              >
                Démarrer maintenant
                <ArrowRight width={20} height={20} strokeWidth={2} />
              </button>
              <button 
                className="btn-landing-hero-secondary"
                onClick={() => navigate('/login')}
              >
                Se connecter
              </button>
            </div>
            <p className="landing-hero-note">
              ✓ Aucune carte de crédit requise • ✓ Configuration en 2 minutes
            </p>
          </div>
          
          {/* Hero Visual */}
          <div className="landing-hero-visual">
            <div className="landing-board-preview">
              <div className="preview-column">
                <div className="preview-column-header">À faire</div>
                <div className="preview-card preview-card-1"></div>
                <div className="preview-card preview-card-2"></div>
              </div>
              <div className="preview-column">
                <div className="preview-column-header">En cours</div>
                <div className="preview-card preview-card-3"></div>
              </div>
              <div className="preview-column">
                <div className="preview-column-header">Terminé</div>
                <div className="preview-card preview-card-4"></div>
                <div className="preview-card preview-card-5"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="landing-features">
        <div className="landing-container">
          <div className="landing-section-header">
            <h2>Tout ce dont vous avez besoin pour réussir</h2>
            <p>Ventilio vous offre des fonctionnalités puissantes pour une gestion de projet efficace</p>
          </div>
          
          <div className="landing-features-grid">
            {features.map((feature, index) => (
              <div key={index} className="landing-feature-card">
                <div className="landing-feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="landing-benefits">
        <div className="landing-container">
          <div className="landing-benefits-content">
            <div className="landing-benefits-text">
              <h2>Pourquoi choisir Ventilio ?</h2>
              <p className="landing-benefits-intro">
                La solution Kanban française qui combine toutes les fonctionnalités des leaders du marché 
                avec une conformité RGPD totale et des tarifs jusqu'à 70% plus avantageux. 
                Vos données restent en France, votre budget aussi.
              </p>
              <ul className="landing-benefits-list">
                {benefits.map((benefit, index) => (
                  <li key={index}>
                    <Check width={20} height={20} strokeWidth={2} />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="landing-benefits-visual">
              <div className="benefits-card">
                <div className="benefits-card-header">
                  <div className="benefits-avatar-group">
                    <div className="benefits-avatar">A</div>
                    <div className="benefits-avatar">B</div>
                    <div className="benefits-avatar">C</div>
                  </div>
                  <span className="benefits-badge">3 membres</span>
                </div>
                <div className="benefits-card-body">
                  <div className="benefits-label">Design</div>
                  <div className="benefits-label">Urgent</div>
                  <div className="benefits-progress">
                    <div className="benefits-progress-bar"></div>
                  </div>
                  <div className="benefits-stats">
                    <span>5/8 tâches</span>
                    <span>2 jours restants</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-cta">
        <div className="landing-container">
          <div className="landing-cta-content">
            <h2>Prêt à transformer votre gestion de projet ?</h2>
            <p>Rejoignez des milliers d'équipes qui font confiance à Ventilio</p>
            <button 
              className="btn-landing-cta"
              onClick={() => navigate('/register')}
            >
              Commencer gratuitement
              <FastArrowRight width={24} height={24} strokeWidth={2} />
            </button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};
